import { delay } from 'redux-saga'
import normalize from 'jsonapi-normalizer'
import T from 'i18n-react'
import {
  takeLatest,
  take,
  fork,
  all,
  call,
  select,
  put,
} from 'redux-saga/effects'

import {
  buildUrl,
} from 'helpers/actionHelper'

import {
  getRequest,
  post,
} from 'helpers/RequestUtil'

import {
  countSelector,
  pageSizeSelector,
  currentPageSelector,
  searchValueSelector,
  editSelector,
} from './selectors'

import {
  CHANGE_EDIT,
  CHANGE_CURRENT_PAGE,
  CHANGE_PAGE_SIZE,
  CHANGE_SEARCH_VALUE,
  CHANGE_SELECT_PRODUCT,
  FETCH_PRODUCTS,
  POST_PRODUCT
} from './constants'

import {
  success,
  error,
} from 'containers/App/Notification/actions'

import {
  setActiveDialog,
} from 'containers/Admin/Product/Product/actions'

import {
  updateEdit,
  updatePageSize,
  updateCountSize,
  updateCurrentPage,
  updateSearchValue,
  updateSelectProduct,
  clearProductList,
  updateProductList,
  fetchProductsStart,
  fetchProductsSuccess,
  fetchProductsError,
  startFetchProductsAsync,
  postProductStart,
  postProductSuccess,
  postProductError,
  startPostProductAsync,
} from './actions'

import {
  updateTablesAction,
} from 'containers/Entity/actions'

function* postProduct({id, enable, ...value}) {
  try {
    yield put(startPostProductAsync())
    yield put(postProductStart())
    const data = yield call(
      post,
      'skus',
      {
        productHierarchyId: id,
        enable: !enable
      },
      '/api/fulcrum/product/skus'
    )
    const normalized = {
      result: { skus: [id] },
      entities: { skus: { [id]: { id, enable: !enable, ...value } } }
    }
    yield put(updateTablesAction(normalized))
    yield put(postProductSuccess())
    yield put(success(T.translate(
      'pages.admin.products.notification.post_success',
      {
        name: value.sku,
        status: !enable ? 'Enabled' : 'Disabled'
      }
    )))
    return data
  } catch(err) {
    yield put(postProductError(err))
    yield put(error(T.translate('pages.admin.products.notification.post_error', {name: value.sku})))
  }
}

function* fetchProducts(url) {
  try {
    yield put(startFetchProductsAsync())
    yield put(fetchProductsStart())
    const data = yield call(
      getRequest,
      url
    )
    const normalized = normalize(data)
    yield put(updateTablesAction(normalized))
    yield put(fetchProductsSuccess())
    return data
  } catch(error) {
    yield put(fetchProductsError(error))
  }
}

function* handlePostProduct(action) {
  const { value } = action
  yield call(postProduct, value)
}

function* handleFetchProducts() {
  let params = []
  yield call(delay, 500)
  const edit = yield select(editSelector)
  const page = yield select(currentPageSelector)
  const size = yield select(pageSizeSelector)
  const search = yield select(searchValueSelector)

  if (!edit)
    params = [...params, { type: 'enable', value: true }]
  if (search)
    params = [...params, { type: 'search', value: search }]

  const url = buildUrl('/api/fulcrum/product/skus', ...params)
    .concat(`&page[number]=${page}&page[size]=${size}`)

  const data = yield call(fetchProducts, url)
  const normalized = normalize(data)
  const { result: { skus } } = normalized
  yield put(updateCountSize(data.meta.totalCount))
  yield put(updateProductList(skus))
}

function* handleChangePageSize(action) {
  const { size } = action
  const page = yield select(currentPageSelector)
  const prevSize = yield select(pageSizeSelector)
  let newPage = Math.floor((page - 1) * prevSize / size)
  newPage = (newPage > 0)
    ? newPage
    : newPage + 1

  yield put(updatePageSize(size))
  yield put(updateCurrentPage(newPage))
  yield fork(handleFetchProducts)
}

function* handleChangeCurrentPage(action) {
  const { value } = action
  const page = yield select(currentPageSelector)
  yield put(updateCurrentPage(page + value))
  yield fork(handleFetchProducts)
}

function* handleSearchChange(action) {
  const { value } = action
  yield put(updateCurrentPage(1))
  yield put(clearProductList())
  yield put(updateSearchValue(value))
  yield fork(handleFetchProducts)
}

function* handleChangeSelectProduct(action) {
  const { id } = action
  yield put(updateSelectProduct(id))
  yield put(setActiveDialog())
}

function* handleChangeEdit(action) {
  const { enable } = action
  yield put(updateCurrentPage(1))
  yield put(updateEdit(enable))
  yield put(clearProductList())
  yield fork(handleFetchProducts)
}

function* watchChangeSelectProduct() {
  yield takeLatest(CHANGE_SELECT_PRODUCT, handleChangeSelectProduct)
}

function* watchPostProduct() {
  yield takeLatest(POST_PRODUCT, handlePostProduct)
}

function* watchFetchProducts() {
  yield takeLatest(FETCH_PRODUCTS, handleFetchProducts)
}

function* watchChangePageSize() {
  yield takeLatest(CHANGE_PAGE_SIZE, handleChangePageSize)
}

function* watchChangeCurrentPage() {
  yield takeLatest(CHANGE_CURRENT_PAGE, handleChangeCurrentPage)
}

function* watchSearchChange() {
  yield takeLatest(CHANGE_SEARCH_VALUE, handleSearchChange)
}

function* watchChangeEdit() {
  yield takeLatest(CHANGE_EDIT, handleChangeEdit)
}

function* loadSagas() {
  yield all([
    fork(watchFetchProducts),
    fork(watchChangePageSize),
    fork(watchChangeCurrentPage),
    fork(watchSearchChange),
    fork(watchPostProduct),
    fork(watchChangeSelectProduct),
    fork(watchChangeEdit)
  ])
}

export default loadSagas
